import React, { useState, useEffect } from 'react';
import { ArrowRight, Play, Star, Menu, X, Zap, Users, ChevronLeft, ChevronRight, Camera, Image, Package, ShoppingCart, Truck, Shield, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const QuickCommerce = () => {
  const { language } = useLanguage();
  const [quickCommerceData, setQuickCommerceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchQuickCommerceData();
  }, []);

  const fetchQuickCommerceData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/q_commerce/data`);
      const result = await response.json();
      
      if (result && result.id) {
        setQuickCommerceData(result);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching Quick Commerce data:', error);
      setLoading(false);
    }
  };

  const MEDIA_BASE_URL = 'https://kooturavu.tn.gov.in';

  const handleNextImage = () => {
    if (quickCommerceData?.products) {
      setCurrentImageIndex((prev) => (prev + 1) % quickCommerceData.products.length);
    }
  };

  const handlePrevImage = () => {
    if (quickCommerceData?.products) {
      setCurrentImageIndex((prev) => (prev - 1 + quickCommerceData.products.length) % quickCommerceData.products.length);
    }
  };

  // Function to handle app opening with deep link
  const handleAppLink = (deepLink, playStoreLink) => {
    // Try to open the app
    window.location.href = deepLink;
    
    // Fallback to Play Store after a short delay if app doesn't open
    setTimeout(() => {
      window.location.href = playStoreLink;
    }, 1500);
  };

  const platforms = [
    { name: 'Blinkit', logo: '🛒', color: 'bg-yellow-400' },
  ];

  const content = {
    en: {
      header: "Quick Commerce",
      overview: "Overview",
      productShowcase: "Product Showcase",
      viewAll: "View All Products",
      allProducts: "All Products",
      locatePlatforms: "Locate on quick commerce website",
      availablePlatforms: "Available on all major platforms"
    },
    ta: {
      header: "விரைவு வர்த்தகம்",
      overview: "மேலோட்டம்",
      productShowcase: "தயாரிப்பு காட்சி",
      viewAll: "அனைத்து தயாரிப்புகளையும் பார்க்கவும்",
      allProducts: "அனைத்து தயாரிப்புகள்",
      locatePlatforms: "விரைவு வர்த்தக இணையதளத்தில் கண்டறியவும்",
      availablePlatforms: "அனைத்து முக்கிய தளங்களிலும் கிடைக்கும்"
    }
  };

  const t = content[language];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!quickCommerceData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">No Quick Commerce data available</div>
      </div>
    );
  }

  const products = quickCommerceData.products?.map(p => ({
    image: `${MEDIA_BASE_URL}/${p.product_image}`,
    name_eng: p.product_name_english,
    name_tamil: p.product_name_tamil
  })) || [];

  const cards = quickCommerceData.cards || [];

  // Icon mapping for cards
  const iconComponents = [Truck, ShoppingCart, Package, Shield];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm py-4 px-4 sm:px-6 pt-6 sm:pt-10">
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-2 sm:gap-[10px]">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <Package className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-800">
              {t.header}
            </h1>
          </div>
        </div>
      </header>

      {/* Overview Section */}
      <section className="py-4 sm:py-6 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
            {t.overview}
          </h2>
          
          <div className="bg-blue-100 rounded-lg border-l-4 border-blue-600 shadow-sm p-4 sm:p-8">
            <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
              {language === 'en' ? quickCommerceData.overview_eng : quickCommerceData.overview_tamil}
            </p>
          </div>
        </div>
      </section>

      {/* Feature Cards Section - With Background Image */}
      <section className="py-8 sm:py-15 px-4 sm:px-6 relative overflow-hidden min-h-[300px] sm:min-h-[400px] flex items-center">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: quickCommerceData.main_image 
              ? `url('${MEDIA_BASE_URL}/${quickCommerceData.main_image}')` 
              : `url('/Images/Gemini_Generated_Image_lgpqdwlgpqdwlgpq.png')`,
            zIndex: 0,
          }}
        ></div>

        <div className="w-full relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-2xl sm:ml-auto mx-auto sm:mx-0 sm:pr-[10px]">
            {cards.map((card, index) => {
              const IconComponent = iconComponents[index % iconComponents.length];
              return (
                <div key={index} className="bg-white rounded-xl shadow-lg p-4 sm:p-5 flex items-start gap-3 transition-shadow duration-300 border border-gray-200">
                  <div className="p-2 sm:p-3 rounded-xl flex-shrink-0 border-2 border-blue-600 text-blue-600">
                    <IconComponent className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm sm:text-base text-gray-800">
                      {language === 'en' ? card.card_content_eng : card.card_content_tamil}
                    </h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Showcase Section - Marquee */}
      {products.length > 0 && (
        <section className="py-8 sm:py-12 bg-white overflow-hidden">
          <style>
            {`
              @keyframes marquee {
                0% {
                  transform: translateX(0);
                }
                100% {
                  transform: translateX(-50%);
                }
              }
            `}
          </style>
          <div 
            className="flex gap-4 sm:gap-8"
            style={{
              animation: `marquee ${products.length * 11}s linear infinite`
            }}
          >
            {/* First set of products */}
            {products.map((product, idx) => (
              <div
                key={`first-${idx}`}
                className="flex-shrink-0 w-56 h-72 sm:w-72 sm:h-80 rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl bg-white flex flex-col"
              >
                <img
                  src={product.image}
                  alt={language === 'en' ? product.name_eng : product.name_tamil}
                  className="w-full h-48 sm:h-60 object-cover"
                />
                <div className="p-2 sm:p-3 bg-white flex-1 flex items-center justify-center">
                  <p className="text-gray-900 font-bold text-sm sm:text-base text-center">
                    {language === 'en' ? product.name_eng : product.name_tamil}
                  </p>
                </div>
              </div>
            ))}
            {/* Duplicate set for seamless loop */}
            {products.map((product, idx) => (
              <div
                key={`second-${idx}`}
                className="flex-shrink-0 w-56 h-72 sm:w-72 sm:h-80 rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl bg-white flex flex-col"
              >
                <img
                  src={product.image}
                  alt={language === 'en' ? product.name_eng : product.name_tamil}
                  className="w-full h-48 sm:h-60 object-cover"
                />
                <div className="p-2 sm:p-3 bg-white flex-1 flex items-center justify-center">
                  <p className="text-gray-900 font-bold text-sm sm:text-base text-center">
                    {language === 'en' ? product.name_eng : product.name_tamil}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          {/* View All Button */}
          <div className="flex justify-center mt-6 sm:mt-8 px-4">
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-blue-600 text-white py-2 px-6 sm:px-8 rounded-lg text-sm sm:text-base font-semibold hover:bg-blue-700 transition-all"
            >
              {t.viewAll}
            </button>
          </div>
        </section>
      )}

      {/* Quick Commerce Platforms Section - Mobile UI */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden opacity-30">
          <div className="absolute top-20 -left-20 w-64 h-64 sm:w-96 sm:h-96 bg-yellow-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 -right-20 w-64 h-64 sm:w-96 sm:h-96 bg-orange-200 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Heading */}
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 tracking-tight">
              {language === 'en' ? 'We are Available On' : 'நாங்கள் கிடைக்கிறோம்'}
            </h2>
            <p className="text-gray-600 text-base sm:text-lg md:text-xl font-medium max-w-2xl mx-auto px-4">
              {language === 'en' ? 'Find our quality products on India\'s leading quick commerce platforms' : 'இந்தியாவின் முன்னணி விரைவு வர்த்தக தளங்களில் எங்கள் தரமான தயாரிப்புகளைக் கண்டறியவும்'}
            </p>
          </div>

          {/* Mobile Phones Container */}
          <div className="flex justify-center items-start gap-8 sm:gap-12 lg:gap-20 flex-wrap">
            
            {/* Blinkit Mobile Phone */}
            <div className="flex flex-col items-center gap-4 sm:gap-6 group">
              <div 
                className="relative cursor-pointer"
                onClick={() => handleAppLink(
                  'blinkit://home',
                  'https://play.google.com/store/apps/details?id=com.grofers.customerapp'
                )}
              >
                {/* Phone Shadow */}
                <div className="absolute inset-0 bg-gradient-to-b from-yellow-400/30 to-yellow-600/30 rounded-[40px] sm:rounded-[56px] blur-2xl opacity-40 transform translate-y-8 scale-95 transition-opacity"></div>
                
                {/* Phone Device */}
                <div className="relative w-[240px] h-[500px] sm:w-[300px] sm:h-[620px] bg-gradient-to-b from-gray-800 to-gray-950 rounded-[40px] sm:rounded-[56px] shadow-2xl p-[10px] sm:p-[14px] border-[4px] sm:border-[6px] border-gray-950 transition-transform duration-500 hover:scale-105">
                  {/* Camera Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[110px] sm:w-[140px] h-[22px] sm:h-[28px] bg-gray-950 rounded-b-[20px] sm:rounded-b-[24px] z-20 flex items-center justify-center gap-2">
                    <div className="w-[5px] h-[5px] sm:w-[6px] sm:h-[6px] bg-gray-700 rounded-full"></div>
                    <div className="w-[8px] h-[8px] sm:w-[10px] sm:h-[10px] bg-gray-800 rounded-full"></div>
                  </div>

                  {/* Power Button */}
                  <div className="absolute -right-[2px] top-[100px] sm:top-[120px] w-[2px] sm:w-[3px] h-[50px] sm:h-[60px] bg-gray-700 rounded-l-lg"></div>
                  {/* Volume Buttons */}
                  <div className="absolute -left-[2px] top-[80px] sm:top-[100px] w-[2px] sm:w-[3px] h-[30px] sm:h-[40px] bg-gray-700 rounded-r-lg"></div>
                  <div className="absolute -left-[2px] top-[120px] sm:top-[150px] w-[2px] sm:w-[3px] h-[30px] sm:h-[40px] bg-gray-700 rounded-r-lg"></div>
                  
                  {/* Screen */}
                  <div className="w-full h-full bg-white rounded-[32px] sm:rounded-[44px] overflow-hidden relative shadow-inner">
                    {/* Status Bar */}
                    <div className="bg-black px-4 sm:px-6 py-[8px] sm:py-[10px] flex justify-between items-center">
                      <span className="text-white text-[12px] sm:text-[15px] font-semibold">9:41</span>
                      <div className="flex gap-[4px] sm:gap-[6px] items-center text-white text-[10px] sm:text-xs">
                        <svg className="w-[14px] h-[14px] sm:w-[17px] sm:h-[17px]" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M2 22h20V2z"/>
                        </svg>
                        <svg className="w-[12px] h-[12px] sm:w-[15px] sm:h-[15px]" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M11.5 1a.5.5 0 0 1 .5.5v12a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-12a.5.5 0 0 1 .5-.5h7z"/>
                          <path d="M14 4v7a1 1 0 0 1-1 1h-1v2h1a3 3 0 0 0 3-3V4h-2z"/>
                        </svg>
                        <span className="font-bold text-[10px] sm:text-xs">100%</span>
                      </div>
                    </div>
                    
                    {/* App Content - Product Listing Screen */}
                    <div className="h-[calc(100%-36px)] sm:h-[calc(100%-44px)] bg-white flex flex-col">
                      {/* Blinkit Header */}
                      <div className="bg-yellow-400 px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between shadow-sm">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-900" />
                          <span className="font-bold text-base sm:text-xl text-gray-900">blinkit</span>
                        </div>
                        <div className="relative">
                          <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 text-gray-900" />
                          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full border border-yellow-400"></div>
                        </div>
                      </div>
                      
                      {/* Product Grid */}
                      <div className="flex-1 overflow-y-auto p-2 sm:p-3 bg-gray-50">
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                          {products.slice(0, 6).map((product, idx) => (
                            <div key={idx} className="bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden shadow-sm flex flex-col">
                              <div className="relative">
                                <img
                                  src={product.image}
                                  alt={product.name_eng}
                                  className="w-full h-20 sm:h-24 object-cover"
                                />
                                <div className="absolute top-1 right-1 bg-green-500 text-white text-[6px] sm:text-[7px] font-bold px-1 sm:px-1.5 py-0.5 rounded">
                                  IN STOCK
                                </div>
                              </div>
                              <div className="p-1.5 sm:p-2.5 flex flex-col flex-1">
                                <p className="text-[7px] sm:text-[9px] font-bold text-gray-900 leading-tight h-6 sm:h-7 overflow-hidden flex-1 mb-1">
                                  {language === 'en' ? product.name_eng : product.name_tamil}
                                </p>
                                <button className="w-full bg-yellow-400 text-gray-900 text-[7px] sm:text-[8px] font-bold py-0.5 sm:py-1 rounded">
                                  ADD
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Bottom Navigation */}
                      <div className="bg-white border-t border-gray-200 px-4 sm:px-6 py-2 sm:py-3 flex justify-around">
                        <div className="text-center">
                          <div className="text-yellow-500 text-sm sm:text-base mb-0.5">🏠</div>
                          <span className="text-[7px] sm:text-[8px] font-semibold text-gray-900">Home</span>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-400 text-sm sm:text-base mb-0.5">🔍</div>
                          <span className="text-[7px] sm:text-[8px] font-medium text-gray-500">Search</span>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-400 text-sm sm:text-base mb-0.5">🛒</div>
                          <span className="text-[7px] sm:text-[8px] font-medium text-gray-500">Cart</span>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-400 text-sm sm:text-base mb-0.5">👤</div>
                          <span className="text-[7px] sm:text-[8px] font-medium text-gray-500">Account</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* App Info & GET Button */}
              <div className="flex flex-col items-center gap-2 sm:gap-3">
                <button
                  onClick={() => handleAppLink(
                    'blinkit://home',
                    'https://play.google.com/store/apps/details?id=com.grofers.customerapp'
                  )}
                  className="bg-gray-900 text-white px-6 sm:px-10 py-2.5 sm:py-3.5 rounded-xl font-bold text-xs sm:text-sm flex items-center gap-2 sm:gap-3 shadow-lg hover:bg-gray-800 hover:shadow-xl transition-all"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <span>GET ON PLAY STORE</span>
                </button>
              </div>
            </div>

            {/* Instamart Mobile Phone */}
            <div className="flex flex-col items-center gap-4 sm:gap-6 group">
              <div 
                className="relative cursor-pointer"
                onClick={() => handleAppLink(
                  'swiggy://instamart',
                  'https://play.google.com/store/apps/details?id=in.swiggy.android'
                )}
              >
                {/* Phone Shadow */}
                <div className="absolute inset-0 bg-gradient-to-b from-orange-400/30 to-orange-600/30 rounded-[40px] sm:rounded-[56px] blur-2xl opacity-40 transform translate-y-8 scale-95 transition-opacity"></div>
                
                {/* Phone Device */}
                <div className="relative w-[240px] h-[500px] sm:w-[300px] sm:h-[620px] bg-gradient-to-b from-gray-800 to-gray-950 rounded-[40px] sm:rounded-[56px] shadow-2xl p-[10px] sm:p-[14px] border-[4px] sm:border-[6px] border-gray-950 transition-transform duration-500 hover:scale-105">
                  {/* Camera Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[110px] sm:w-[140px] h-[22px] sm:h-[28px] bg-gray-950 rounded-b-[20px] sm:rounded-b-[24px] z-20 flex items-center justify-center gap-2">
                    <div className="w-[5px] h-[5px] sm:w-[6px] sm:h-[6px] bg-gray-700 rounded-full"></div>
                    <div className="w-[8px] h-[8px] sm:w-[10px] sm:h-[10px] bg-gray-800 rounded-full"></div>
                  </div>

                  {/* Power Button */}
                  <div className="absolute -right-[2px] top-[100px] sm:top-[120px] w-[2px] sm:w-[3px] h-[50px] sm:h-[60px] bg-gray-700 rounded-l-lg"></div>
                  {/* Volume Buttons */}
                  <div className="absolute -left-[2px] top-[80px] sm:top-[100px] w-[2px] sm:w-[3px] h-[30px] sm:h-[40px] bg-gray-700 rounded-r-lg"></div>
                  <div className="absolute -left-[2px] top-[120px] sm:top-[150px] w-[2px] sm:w-[3px] h-[30px] sm:h-[40px] bg-gray-700 rounded-r-lg"></div>
                  
                  {/* Screen */}
                  <div className="w-full h-full bg-white rounded-[32px] sm:rounded-[44px] overflow-hidden relative shadow-inner">
                    {/* Status Bar */}
                    <div className="bg-black px-4 sm:px-6 py-[8px] sm:py-[10px] flex justify-between items-center">
                      <span className="text-white text-[12px] sm:text-[15px] font-semibold">9:41</span>
                      <div className="flex gap-[4px] sm:gap-[6px] items-center text-white text-[10px] sm:text-xs">
                        <svg className="w-[14px] h-[14px] sm:w-[17px] sm:h-[17px]" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M2 22h20V2z"/>
                        </svg>
                        <svg className="w-[12px] h-[12px] sm:w-[15px] sm:h-[15px]" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M11.5 1a.5.5 0 0 1 .5.5v12a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-12a.5.5 0 0 1 .5-.5h7z"/>
                          <path d="M14 4v7a1 1 0 0 1-1 1h-1v2h1a3 3 0 0 0 3-3V4h-2z"/>
                        </svg>
                        <span className="font-bold text-[10px] sm:text-xs">100%</span>
                      </div>
                    </div>
                    
                    {/* App Content - Product Listing Screen */}
                    <div className="h-[calc(100%-36px)] sm:h-[calc(100%-44px)] bg-white flex flex-col">
                      {/* Instamart Header */}
                      <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between shadow-md">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                          <span className="font-bold text-base sm:text-xl text-white">Instamart</span>
                        </div>
                        <div className="relative">
                          <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-white rounded-full border border-orange-500"></div>
                        </div>
                      </div>
                      
                      {/* Product Grid */}
                      <div className="flex-1 overflow-y-auto p-2 sm:p-3 bg-gray-50">
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                          {products.slice(0, 6).map((product, idx) => (
                            <div key={idx} className="bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden shadow-sm flex flex-col">
                              <div className="relative">
                                <img
                                  src={product.image}
                                  alt={product.name_eng}
                                  className="w-full h-20 sm:h-24 object-cover"
                                />
                                <div className="absolute top-1 right-1 bg-green-500 text-white text-[6px] sm:text-[7px] font-bold px-1 sm:px-1.5 py-0.5 rounded">
                                  AVAILABLE
                                </div>
                              </div>
                              <div className="p-1.5 sm:p-2.5 flex flex-col flex-1">
                                <p className="text-[7px] sm:text-[9px] font-bold text-gray-900 leading-tight h-6 sm:h-7 overflow-hidden flex-1 mb-1">
                                  {language === 'en' ? product.name_eng : product.name_tamil}
                                </p>
                                <button className="w-full bg-orange-500 text-white text-[7px] sm:text-[8px] font-bold py-0.5 sm:py-1 rounded">
                                  ADD
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Bottom Navigation */}
                      <div className="bg-white border-t border-gray-200 px-4 sm:px-6 py-2 sm:py-3 flex justify-around">
                        <div className="text-center">
                          <div className="text-orange-500 text-sm sm:text-base mb-0.5">🏠</div>
                          <span className="text-[7px] sm:text-[8px] font-semibold text-gray-900">Home</span>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-400 text-sm sm:text-base mb-0.5">🔍</div>
                          <span className="text-[7px] sm:text-[8px] font-medium text-gray-500">Search</span>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-400 text-sm sm:text-base mb-0.5">🛒</div>
                          <span className="text-[7px] sm:text-[8px] font-medium text-gray-500">Cart</span>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-400 text-sm sm:text-base mb-0.5">👤</div>
                          <span className="text-[7px] sm:text-[8px] font-medium text-gray-500">Account</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* App Info & GET Button */}
              <div className="flex flex-col items-center gap-2 sm:gap-3">
                <button
                  onClick={() => handleAppLink(
                    'swiggy://instamart',
                    'https://play.google.com/store/apps/details?id=in.swiggy.android'
                  )}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 sm:px-10 py-2.5 sm:py-3.5 rounded-xl font-bold text-xs sm:text-sm flex items-center gap-2 sm:gap-3 shadow-lg hover:from-orange-600 hover:to-orange-700 hover:shadow-xl transition-all"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <span>GET ON PLAY STORE</span>
                </button>
              </div>
            </div>

          </div>

          {/* Bottom Info */}
          <div className="text-center mt-10 sm:mt-16 px-4">
            <p className="text-gray-500 text-xs sm:text-sm font-medium">
              {language === 'en' ? 'Download the apps and discover our complete product range' : 'பயன்பாடுகளைப் பதிவிறக்கி, எங்கள் முழுமையான தயாரிப்பு வரம்பைக் கண்டறியவும்'}
            </p>
          </div>
        </div>
      </section>

      {/* Modal - View All Products */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-5xl rounded-lg overflow-hidden shadow-xl max-h-[90vh] flex flex-col">
            
            {/* Header */}
            <div className="bg-blue-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
              <h3 className="text-white text-xl sm:text-2xl font-bold">{t.allProducts}</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-white font-bold text-2xl hover:text-gray-200"
              >
                ×
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {products.map((product, idx) => (
                  <div
                    key={idx}
                    className="border rounded-lg overflow-hidden flex flex-col items-center bg-gray-50 hover:shadow-lg transition"
                  >
                    <img
                      src={product.image}
                      alt={language === 'en' ? product.name_eng : product.name_tamil}
                      className="w-full h-32 sm:h-40 object-cover"
                    />
                    <div className="p-2 sm:p-3 text-center">
                      <p className="text-gray-800 font-semibold text-sm sm:text-base">
                        {language === 'en' ? product.name_eng : product.name_tamil}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default QuickCommerce;